import { ApiClient } from "@/api/ApiClient";
import { getTenantId } from "@/utils/Auth";
import { isEDocumentAtvAccepted } from "@/utils/edocumentAtvStatus";
import {
    DOCUMENT_LIMIT_CATALOG,
    DocumentLimits,
    currentMonthPrefix,
    getDocumentLimit,
} from "@/security/documentLimits.catalog";

export type DocumentUsage = {
    documentTypeId: string;
    label: string;
    limit: number;
    used: number;
    available: number;
};

/**
 * Cuenta los documentos de un tipo consumidos en el mes ("YYYY-MM"): para tipos
 * electrónicos solo cuentan los aceptados por Hacienda; para cotizaciones, todas.
 */
export async function countUsedByMonth(documentTypeId: string, monthPrefix: string): Promise<number> {
    const node = DOCUMENT_LIMIT_CATALOG.find(n => n.id === documentTypeId);
    if (!node) return 0;
    const tenantId = await getTenantId();
    const model = (ApiClient.models as Record<string, any>)[documentTypeId];
    const queryFn = model?.[node.queryField];
    if (!queryFn) return 0;

    const selectionSet = node.frontendOnly ? ["id"] : ["id", "edocument_status_payload"];
    let count = 0;
    let nextToken: string | undefined = undefined;
    do {
        const { data, nextToken: nt } = await queryFn(
            { owner: tenantId, [node.dateField]: { beginsWith: monthPrefix } },
            { selectionSet, limit: 1000, nextToken },
        );
        for (const item of data || []) {
            if (node.frontendOnly || isEDocumentAtvAccepted(item.edocument_status_payload)) count++;
        }
        nextToken = nt || undefined;
    } while (nextToken);
    return count;
}

/**
 * Uso del mes en curso para cada tipo con límite configurado (> 0) en el plan.
 * Devuelve límite, consumidos y disponibles para mostrarlos en la administración de suscripción.
 */
export async function loadMonthlyDocumentUsage(limits: DocumentLimits): Promise<DocumentUsage[]> {
    if (!limits) return [];
    const monthPrefix = currentMonthPrefix();
    const nodes = DOCUMENT_LIMIT_CATALOG.filter(n => getDocumentLimit(limits, n.id) > 0);
    return Promise.all(
        nodes.map(async (node) => {
            const limit = getDocumentLimit(limits, node.id);
            const used = await countUsedByMonth(node.id, monthPrefix);
            return {
                documentTypeId: node.id,
                label: node.label,
                limit,
                used,
                available: Math.max(0, limit - used),
            };
        }),
    );
}
